import { ChevronsUpDown } from "lucide-react";
import { Fragment, useMemo } from "react";
import { Link } from "react-router-dom";
import { usePermissions } from "../../../../../contexts/permissionsContext.js";
import { cn } from "../../../../lib/utils.js";
import { SIDEBAR_USER_DROPDOWN_ITEMS } from "../constants/sidebar-user-dropdown-items.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../ui/dropdown-menu.js";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem as SidebarMenuItemPrimitive,
} from "../../../ui/sidebar.js";
import { UserAvatar } from "../../../ui/user-avatar.js";

const dropdownMenuContentClassName =
  "z-[1600] min-w-56 rounded-lg border border-gray-800 bg-black p-1 text-white shadow-lg";

const dropdownMenuItemClassName =
  "cursor-pointer rounded-lg text-[13px] font-medium text-gray-400 transition-colors hover:bg-gray-900 hover:text-white focus:bg-gray-900 focus:text-white data-[highlighted]:bg-gray-900 data-[highlighted]:text-white [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-current";

/**
 * Rodapé da sidebar (itens de navegação + perfil do usuário).
 */
export function SidebarFooter({
  footerItems,
  collapsed,
  isMobile,
  onNavigate,
  onLogout,
}) {
  const usuario = useMemo(() => {
    try {
      const stored = localStorage.getItem("user_token");
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  }, []);

  const { hasPermissionAction } = usePermissions();

  const userName = usuario?.name || "Usuário";
  const userEmail = usuario?.email || usuario?.login || "";
  const showUserDetails = !collapsed || isMobile;

  const visibleDropdownItems = useMemo(
    () =>
      SIDEBAR_USER_DROPDOWN_ITEMS.filter((item) => {
        if (!item.permission) {
          return true;
        }
        return hasPermissionAction(
          item.permission,
          item.actionPermission || "islist",
        );
      }),
    [hasPermissionAction],
  );

  const handleDropdownItemClick = (item) => {
    if (item.action === "logout") {
      onLogout();
      return;
    }

    if (item.action === "navigate" && item.href) {
      onNavigate(item.href, item.label);
    }
  };

  return (
    <div className="border-t border-gray-800 px-2 py-2">
      <SidebarMenu className="flex flex-col gap-0.5">
        {footerItems.map((item) => {
          const Icon = item.icon;
          return (
            <SidebarMenuItemPrimitive key={item.id || item.href}>
              <SidebarMenuButton
                asChild
                tooltip={collapsed && !isMobile ? item.label : undefined}
                className={cn(
                  "flex h-10 w-full items-center rounded-lg p-0 text-[13px] font-medium text-gray-400 transition-colors hover:!bg-gray-900 hover:!text-white active:!bg-gray-900 active:!text-white",
                  collapsed && !isMobile
                    ? "justify-center px-0"
                    : "justify-start gap-2.5 px-3",
                )}
              >
                <Link
                  to={item.href}
                  onClick={() => onNavigate(item.href, item.label)}
                >
                  <Icon size={20} className="flex-shrink-0" />
                  {showUserDetails && (
                    <span className="truncate leading-tight">{item.label}</span>
                  )}
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItemPrimitive>
          );
        })}
      </SidebarMenu>

      <div className="mt-2 border-t border-gray-800 pt-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(
                "flex w-full items-center rounded-lg text-left transition-colors hover:bg-gray-900",
                showUserDetails
                  ? "gap-2.5 px-3 py-2"
                  : "justify-center px-0 py-2",
              )}
              aria-label="Menu do usuário"
            >
              <UserAvatar name={userName} className="h-8 w-8 shrink-0" />
              {showUserDetails && (
                <>
                  <div className="grid min-w-0 flex-1 text-left leading-tight">
                    <span className="truncate text-sm font-semibold text-white">
                      {userName}
                    </span>
                    {userEmail ? (
                      <span className="truncate text-xs text-gray-400">
                        {userEmail}
                      </span>
                    ) : null}
                  </div>
                  <ChevronsUpDown
                    size={16}
                    className="ml-auto shrink-0 text-gray-400"
                  />
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="right"
            align={showUserDetails ? "start" : "center"}
            className={dropdownMenuContentClassName}
          >
            {visibleDropdownItems.map((item) => {
              const Icon = item.icon;

              return (
                <Fragment key={item.id}>
                  {item.separatorBefore ? (
                    <DropdownMenuSeparator className="bg-gray-800" />
                  ) : null}
                  <DropdownMenuItem
                    className={dropdownMenuItemClassName}
                    onClick={() => handleDropdownItemClick(item)}
                  >
                    <Icon />
                    <span>{item.label}</span>
                  </DropdownMenuItem>
                </Fragment>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
